import React from 'react';
import { ArrowLeft, Tag, CheckCircle2, Sparkles, Shield, Zap } from 'lucide-react';

const CHANGELOG = [
  {
    version: "2026-01-15",
    date: "January 15, 2026",
    tag: "Current",
    tagColor: "bg-emerald-100 text-emerald-700",
    summary: "Initial public API release with full Ceipal integration support.",
    changes: [
      { type: "feature", text: "Region-scoped API keys (NA, UK_EMEA, APAC) with admin-only generation" },
      { type: "feature", text: "POST /v1/jobs — Create jobs with auto JD analysis trigger" },
      { type: "feature", text: "GET /v1/jobs/{id}/jd-analysis — JD Intelligence with public share URL" },
      { type: "feature", text: "GET /v1/jobs/{id}/market-intelligence — Market Intelligence with ?generate=true support" },
      { type: "feature", text: "GET /v1/jobs/{id}/discovery — Discovery Call Questions with ?generate=true" },
      { type: "feature", text: "GET /v1/jobs/{id}/sourcing — Smart Sourcing Strategy with ?generate=true" },
      { type: "feature", text: "POST /v1/jobs/{id}/interviews — Create AI interview links for candidates" },
      { type: "feature", text: "GET /v1/jobs/{id}/share-links — All public shareable URLs for embedding" },
      { type: "feature", text: "POST /v1/jobs/{id}/embed — Generate embeddable intelligence page URL" },
      { type: "feature", text: "Webhook notifications for jd_analysis, market_intelligence, discovery, and sourcing completion" },
      { type: "feature", text: "Test webhook button for verifying Ceipal webhook receiver" },
      { type: "feature", text: "Sandbox mode with instant mock data — no LLM costs" },
      { type: "feature", text: "Interactive API documentation with Try It testing" },
      { type: "feature", text: "API usage analytics dashboard with daily/hourly charts" },
      { type: "security", text: "X-API-Key header authentication on all endpoints" },
      { type: "security", text: "100 requests/minute rate limiting per API key" },
      { type: "security", text: "Date-based API versioning (X-API-Version header)" },
      { type: "performance", text: "Background task processing — all LLM calls are async, no blocking" },
    ]
  },
];

const typeIcons = {
  feature: Sparkles,
  security: Shield,
  performance: Zap,
};
const typeColors = {
  feature: "text-indigo-600",
  security: "text-amber-600",
  performance: "text-emerald-600",
};

export default function ChangelogPage() {
  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">H</div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">API Changelog</h1>
              <p className="text-xs text-gray-500">HireFast AI — Version History</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/developers" className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
              API Docs
            </a>
            <a href="/login" className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
              <ArrowLeft className="h-3 w-3" /> Login
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="space-y-8">
          {CHANGELOG.map((release, ri) => (
            <div key={ri} className="relative">
              {/* Timeline dot */}
              {ri < CHANGELOG.length - 1 && <div className="absolute left-[15px] top-10 bottom-0 w-px bg-gray-200" />}

              <div className="flex gap-4">
                <div className="shrink-0 w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center mt-0.5 z-10">
                  <Tag className="h-4 w-4 text-indigo-600" />
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-base font-bold text-gray-900 font-mono">{release.version}</h2>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${release.tagColor}`}>{release.tag}</span>
                    <span className="text-xs text-gray-400">{release.date}</span>
                  </div>

                  <p className="text-sm text-gray-600 mb-4">{release.summary}</p>

                  <div className="bg-white border rounded-xl p-4 space-y-2">
                    {release.changes.map((ch, ci) => {
                      const Icon = typeIcons[ch.type] || CheckCircle2;
                      const color = typeColors[ch.type] || "text-gray-500";
                      return (
                        <div key={ci} className="flex items-start gap-2.5">
                          <Icon className={`h-4 w-4 ${color} shrink-0 mt-0.5`} />
                          <p className="text-sm text-gray-700">{ch.text}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center border-t pt-6">
          <p className="text-xs text-gray-400">Subscribe to API updates — contact hirefast@tekwissen.in</p>
        </div>
      </main>
    </div>
  );
}
